/**
 * The units a Rebate Report line sold (flag `condition_pricing`, MOT-412): one
 * chip per per-unit listing (open box, demo, display) whose sale the order pull
 * tied to a sold line (MOT-384), naming its condition, grade and serial. The
 * report only sends `soldUnits` with the flag on and a unit behind the line, so
 * with the flag off this renders nothing and the line looks as it did.
 */
import React from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { StatusChip } from '../ui';

export interface RebateReportSoldUnit {
  unitId: string;
  orderItemId: string;
  orderNumber: string;
  condition: 'new' | 'open_box' | 'demo' | 'display';
  grade: 'like_new' | 'excellent' | 'good' | 'fair' | null;
  serial: string | null;
}

const SHOWN = 3;

export const RebateLineSoldUnits: React.FC<{ units?: RebateReportSoldUnit[] }> = ({ units }) => {
  const { t } = useAppTranslation();
  if (!units || units.length === 0) return null;

  const label = (unit: RebateReportSoldUnit) => [
    t(`store.skuCrosswalk.condition.${unit.condition}`),
    unit.grade ? t(`store.storeUnits.grade.${unit.grade}`) : null,
    unit.serial ? t('store.storeUnits.serialShort', { serial: unit.serial }) : null,
  ].filter(Boolean).join(' · ');

  const rest = units.length - SHOWN;
  return (
    <Box
      aria-label={t('store.storeUnits.reportLine.label')}
      sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}
    >
      {units.slice(0, SHOWN).map((unit) => (
        <Tooltip key={unit.unitId} title={t('store.storeUnits.reportLine.tip', { order: unit.orderNumber || '—' })}>
          <span>
            <StatusChip label={label(unit)} tone="info" variant="outlined" />
          </span>
        </Tooltip>
      ))}
      {rest > 0 && (
        <Tooltip title={units.slice(SHOWN).map(label).join(', ')}>
          <Typography variant="caption" color="text.secondary" sx={{ cursor: 'default' }}>
            {t('store.storeUnits.reportLine.more', { count: rest })}
          </Typography>
        </Tooltip>
      )}
    </Box>
  );
};
